export type SurfShoreShape = 'cove' | 'open' | 'reef' | 'cliff';

/** Reviewed artwork coordinates in the shared 1000 × 562 scene space. */
export type SurfCoastGeometry = {
  horizonY: number;
  skyRight: number;
  shoreStartY: number;
  edge: readonly (readonly number[])[];
};

/** Smooth interpolation stays within each pair of samples, including curved headlands. */
export function surfCoastEdgeX(geometry: SurfCoastGeometry, y: number): number {
  const points = geometry.edge;
  if (y <= points[0][1]) return points[0][0];
  const last = points[points.length - 1];
  if (y >= last[1]) return last[0];
  let low = 0, high = points.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >>> 1;
    if (points[middle][1] < y) low = middle; else high = middle;
  }
  const i = low;
  const [x0, y0] = points[i], [x1, y1] = points[i + 1];
  const t = (y - y0) / (y1 - y0);
  const slope = (x1 - x0) / (y1 - y0);
  const tangent = (index: number) => {
    const a = points[Math.max(0, index - 1)], b = points[Math.min(points.length - 1, index + 1)];
    const value = (b[0] - a[0]) / (b[1] - a[1]);
    return value * slope <= 0 ? 0 : Math.sign(slope) * Math.min(Math.abs(value), Math.abs(slope) * 2);
  };
  return (2 * t ** 3 - 3 * t * t + 1) * x0 + (t ** 3 - 2 * t * t + t) * (y1 - y0) * tangent(i)
    + (-2 * t ** 3 + 3 * t * t) * x1 + (t ** 3 - t * t) * (y1 - y0) * tangent(i + 1);
}

export const SURF_SHORE_LABELS: Record<SurfShoreShape, string> = {
  cove: 'Curved cove scenery', open: 'Open beach scenery', reef: 'Low rocky coast scenery', cliff: 'Cliff-backed coast scenery',
};
